import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import {
  useSearchParams,
  useActionData,
  useLoaderData,
  Form,
  Link,
  useNavigation,
  useFetcher,
} from "react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { startAuthentication } from "@simplewebauthn/browser";
import { Button } from "~/components/shared/Button";
import { InputField } from "~/components/shared/Form";
import LoadingIndicator from "~/components/shared/LoadingIndicator";
import {
  createSession,
  login,
  passkeyAuthenticationOptions,
  passkeyAuthenticationVerify,
} from "~/session.server";
import { passkeyLoginEnabled } from "~/utils/flags.server";

const Spinner = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    className="animate-spin h-5 w-5 text-jiwe-blue mx-auto"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    {...props}
  >
    <circle
      className="opacity-25"
      cx={12}
      cy={12}
      r={10}
      stroke="currentColor"
      strokeWidth={4}
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const loader = async (_: LoaderFunctionArgs) => {
  return { passkeyLoginEnabled: passkeyLoginEnabled() };
};

export const action = async ({ request }: ActionFunctionArgs) => {
  if (request.method === "POST") {
    const formData = await request.formData();
    const intent = formData.get("intent") as string | null;

    if (intent === "passkey-options") {
      if (!passkeyLoginEnabled()) {
        return Response.json({ error: "Passkey login is not enabled." }, { status: 403 });
      }
      const email = formData.get("email") as string;
      const result = await passkeyAuthenticationOptions({
        email: email || undefined,
      });
      if (result.errors) {
        return Response.json({ intent: "passkey-options", error: result.errors });
      }
      return Response.json({
        intent: "passkey-options",
        challengeId: result.challengeId,
        options: result.options,
      });
    }

    if (intent === "passkey-verify") {
      if (!passkeyLoginEnabled()) {
        return Response.json({ error: "Passkey login is not enabled." }, { status: 403 });
      }
      const challengeId = formData.get("challengeId") as string;
      const credential = JSON.parse(formData.get("credential") as string);
      let redirectTo = (formData.get("redirectTo") as string) || "/";
      redirectTo = redirectTo === "" ? "/tournaments" : redirectTo;
      redirectTo = redirectTo.replace("/my-notifications", "");
      const result = await passkeyAuthenticationVerify({ challengeId, credential });
      if (result.accessToken) {
        return createSession({ accessToken: result.accessToken, redirectTo });
      }
      return Response.json({ intent: "passkey-verify", error: result.errors });
    }

    // Default: password login
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    let redirectTo = formData.get("redirectTo") as string;
    redirectTo = redirectTo === "" ? "/tournaments" : redirectTo;
    redirectTo = redirectTo.replace("/my-notifications", "");
    const result = await login({ email, password });

    if (result.accessToken) {
      const { accessToken } = result;
      return createSession({ accessToken, redirectTo });
    }
    return result.errors;
  }
};

export function meta() {
  return [
    {
      title: "Login - Jiwe eSports",
    },
  ];
}

export default function LoginRoute() {
  const { passkeyLoginEnabled: passkeyEnabled } = useLoaderData<typeof loader>();
  const loginError = useActionData<string>();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const fetcher = useFetcher<{
    intent: string;
    challengeId?: string;
    options?: any;
    error?: string;
  }>();
  const handledChallengeId = useRef<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [passkeySupported, setPasskeySupported] = useState(true);

  useEffect(() => {
    setPasskeySupported(
      typeof window !== "undefined" && !!window.PublicKeyCredential
    );
  }, []);

  useEffect(() => {
    if (loginError) {
      toast.error(loginError);
    }
  }, [loginError]);

  useEffect(() => {
    if (!fetcher.data) return;

    if (fetcher.data.error) {
      toast.error(fetcher.data.error);
      return;
    }

    if (
      fetcher.data.intent === "passkey-options" &&
      fetcher.data.options &&
      fetcher.data.challengeId &&
      handledChallengeId.current !== fetcher.data.challengeId
    ) {
      handledChallengeId.current = fetcher.data.challengeId;
      const { challengeId, options } = fetcher.data;

      (async () => {
        try {
          const credential = await startAuthentication({ optionsJSON: options });
          fetcher.submit(
            {
              intent: "passkey-verify",
              challengeId,
              credential: JSON.stringify(credential),
              redirectTo: searchParams.get("redirectTo") ?? "",
            },
            { method: "post" }
          );
        } catch (err: any) {
          if (
            err?.name === "NotAllowedError" ||
            err?.name === "AbortError"
          ) {
            toast.error("Passkey was cancelled.");
          } else {
            toast.error("Could not use a passkey on this device.");
          }
        }
      })();
    }
  }, [fetcher.data, searchParams]);

  const handlePasskeyClick = () => {
    const email =
      formRef.current?.querySelector<HTMLInputElement>('input[name="email"]')
        ?.value ?? "";
    fetcher.submit({ intent: "passkey-options", email }, { method: "post" });
  };

  return (
    <>
      <LoadingIndicator />
      <div className="flex flex-col-reverse sm:flex-row sm:justify-center sm:items-center sm:content-center mt-[8rem]">
        <div className="mx-[10%]">
          <div className="flex flex-col items-center mb-12 pb-6 border-b-[.5px] border-b-divider dark:border-b-divider-dark">
            <Link to="/">
              <img
                src="/assets/logo.png"
                alt="logo"
                className="h-[4rem] mb-5"
              />
            </Link>
            <h1 className="font-bold text-xl text-center text-grey-border uppercase ml-[1rem]">
              Sign in to your <br />
              <span className="text-jiwe-blue dark:text-jiwe-red">
                Jiwe esports
              </span>
              <br />
              account
            </h1>
          </div>
          <Form method="post" ref={formRef}>
            <input
              type="hidden"
              name="redirectTo"
              value={searchParams.get("redirectTo") ?? undefined}
            />
            <InputField type="email" name="email" label="Email" required />
            <InputField
              type="password"
              name="password"
              label="Password"
              required
            />
            <Button
              type="submit"
              className="bg-jiwe-yellow uppercase w-full mb-8"
              disabled={navigation.state === "submitting"}
            >
              {navigation.state !== "idle" ? <Spinner /> : "Login"}
            </Button>
            <div className="flex flex-col lg:flex-row gap-y-4 items-center font-bold text-jiwe-red dark:text-jiwe-blue">
              <Link to="/forgot-password">Forgot your password?</Link>{" "}
              <span className="hidden lg:block text-grey-darker mx-2">/</span>{" "}
              <Link to="/register">I don't have an account?</Link>
            </div>
          </Form>
          {passkeyEnabled && (
            <>
              <div className="flex items-center my-6 gap-3">
                <hr className="flex-1 border-divider dark:border-divider-dark" />
                <span className="text-sm text-grey-border">or</span>
                <hr className="flex-1 border-divider dark:border-divider-dark" />
              </div>
              <div
                title={
                  !passkeySupported
                    ? "This browser does not support passkeys."
                    : undefined
                }
              >
                <Button
                  type="button"
                  className="w-full uppercase"
                  disabled={!passkeySupported || fetcher.state !== "idle"}
                  onClick={handlePasskeyClick}
                >
                  {fetcher.state !== "idle" ? <Spinner /> : "Continue with passkey"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
